import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { CONSTANTS } from '@livable/saas-react-components';
import { Box, Button, Paper, Stack, Tab, Tabs, Typography } from '@mui/material';
import { LivyPanel } from 'src/components/livy/LivyPanel';
import { LivyAvatar } from 'src/components/livy/LivyAvatar';
import { LivyHelp } from 'src/components/livy/LivyHelp';
import { LivyReports } from 'src/components/livy/LivyReports';
import { LivyHistory } from 'src/components/livy/LivyHistory';
import { LivyTheme } from 'src/components/livy/LivyTheme';
import { observer } from 'mobx-react-lite';
import { canUseLivy, livyUnavailableMessage, useLivyAccess } from 'src/components/livy/access';
import { useStore } from 'src/stores';
import { selectedCompanyName, useLivyChat } from 'src/components/livy/useLivyChat';
import { useResetOnCompanyChange } from 'src/components/livy/useResetOnCompanyChange';
import { SURFACE_STARTERS, SURFACE_SUBTITLE } from 'src/components/livy/LivySurfacePicker';
import { useEngagement } from 'src/components/livy/LivyGreeting';
import { popularReports } from 'src/components/livy/pageHelp';
import { useBillingType } from 'src/components/livy/billingType';
import { isReplayConversation } from 'src/components/livy/history';
import { livyConfig } from 'src/lib/livyTransport';
import { usePageAsk } from 'src/components/livy/pageAsk';
import { LivyPreviewBanner } from 'src/components/livy/LivyPreviewBanner';
import { useNavigate } from 'react-router-dom';
import { freshToken, tokenForCompany } from 'src/lib/livyApi';
/**
 * Full-page Livy for customers: the drawer's Chat, Help and Reports on the whole screen, on the customer
 * surface. Staff reach it from the sidebar's Livy item as a preview of the selected company, which is why
 * their token moves onto that company like the drawer's Customer view.
 */
const LivyCustomerPageBody = observer(function LivyCustomerPageBody() {
  const store = useStore();
  const staff = canUseLivy();
  const access = useLivyAccess(store.account.userProfile.userRole);
  const companyLabel = selectedCompanyName();
  const context = useMemo(() => ({
    route: '/livy/chat'
  }), []);
  const getToken = useCallback(() => staff ? tokenForCompany() : freshToken(), [staff]);
  const billingType = useBillingType();
  const engagement = useEngagement(access);
  const _useState = useState('chat'),
    _useState2 = _slicedToArray(_useState, 2),
    tab = _useState2[0],
    setTab = _useState2[1];
  const _useState3 = useState(false),
    _useState4 = _slicedToArray(_useState3, 2),
    historyOpen = _useState4[0],
    setHistoryOpen = _useState4[1];
  const _useState5 = useState(null),
    _useState6 = _slicedToArray(_useState5, 2),
    queued = _useState6[0],
    setQueued = _useState6[1];
  const _usePageAsk = usePageAsk(),
    _usePageAsk2 = _slicedToArray(_usePageAsk, 2),
    ask = _usePageAsk2[0],
    clearAsk = _usePageAsk2[1];
  const navigate = useNavigate();
  const config = livyConfig();
  const _useLivyChat = useLivyChat({
      config: config !== null && config !== void 0 ? config : {
        endpoint: '',
        region: '',
        identityPoolId: '',
        userPoolId: ''
      },
      getToken,
      context,
      surface: 'pm',
      billingType
    }),
    messages = _useLivyChat.messages,
    streaming = _useLivyChat.streaming,
    error = _useLivyChat.error,
    send = _useLivyChat.send,
    stop = _useLivyChat.stop,
    reset = _useLivyChat.reset,
    retry = _useLivyChat.retry,
    resume = _useLivyChat.resume,
    conversationId = _useLivyChat.conversationId,
    live = _useLivyChat.live,
    startLive = _useLivyChat.startLive;
  useResetOnCompanyChange(reset);
  const readOnly = isReplayConversation(conversationId);

  // A report, or an ask on a kept replay, is a new conversation: sent once the reset has emptied it.
  useEffect(() => {
    if (!queued || messages.length > 0) return;
    setQueued(null);
    void send(queued);
  }, [queued, messages.length, send]);
  const startWith = useCallback(text => {
    setTab('chat');
    if (readOnly) {
      reset();
      setQueued(text);
    } else void send(text);
  }, [readOnly, reset, send]);

  // An ask the launcher handed this page: sent once, then cleared.
  const asked = useRef(false);
  useEffect(() => {
    if (!ask) {
      asked.current = false;
      return;
    }
    if (asked.current || !access || !config || streaming) return;
    asked.current = true;
    clearAsk();
    startWith(ask);
  }, [ask, clearAsk, access, config, streaming, startWith]);
  if (!access || !config) {
    return /*#__PURE__*/React.createElement(Box, {
      sx: {
        p: '24px'
      }
    }, /*#__PURE__*/React.createElement(Typography, {
      variant: "body1"
    }, livyUnavailableMessage));
  }
  return (
    /*#__PURE__*/
    // Grows into the shell's bottom padding so the transcript reaches the window's bottom (as LivyPage).
    React.createElement(Stack, {
      sx: {
        height: "calc(100% + ".concat(CONSTANTS.PAGE_BOTTOM_PADDING, "px)"),
        mb: "-".concat(CONSTANTS.PAGE_BOTTOM_PADDING, "px"),
        minHeight: 0
      }
    }, /*#__PURE__*/React.createElement(Paper, {
      variant: "outlined",
      sx: {
        flex: 1,
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        borderRadius: 0,
        borderWidth: '0 0 0 1px'
      }
    }, /*#__PURE__*/React.createElement(Stack, {
      direction: "row",
      alignItems: "center",
      justifyContent: "space-between",
      sx: {
        px: '16px',
        py: '12px',
        borderBottom: 1,
        borderColor: 'divider',
        flexWrap: 'wrap',
        gap: '10px'
      }
    }, /*#__PURE__*/React.createElement(Stack, {
      direction: "row",
      spacing: "10px",
      alignItems: "center",
      sx: {
        minWidth: 0
      }
    }, /*#__PURE__*/React.createElement(LivyAvatar, {
      size: 30,
      thinking: streaming
    }), /*#__PURE__*/React.createElement(Box, {
      sx: {
        minWidth: 0
      }
    }, /*#__PURE__*/React.createElement(Typography, {
      sx: {
        fontSize: 15,
        fontWeight: 600,
        lineHeight: 1.25
      }
    }, "Livy"), companyLabel && /*#__PURE__*/React.createElement(Typography, {
      noWrap: true,
      sx: {
        fontSize: 11.5,
        color: 'text.secondary'
      }
    }, companyLabel))), /*#__PURE__*/React.createElement(Stack, {
      direction: "row",
      alignItems: "center",
      sx: {
        gap: '8px'
      }
    }, /*#__PURE__*/React.createElement(Button, {
      size: "small",
      onClick: () => setHistoryOpen(true),
      disabled: streaming,
      sx: {
        textTransform: 'none',
        fontSize: 12.5,
        minWidth: 0,
        px: '10px'
      }
    }, "History"), /*#__PURE__*/React.createElement(Button, {
      size: "small",
      onClick: reset,
      disabled: messages.length === 0 || streaming,
      sx: {
        textTransform: 'none',
        fontSize: 12.5,
        minWidth: 0,
        px: '10px'
      }
    }, "New chat"))), staff && /*#__PURE__*/React.createElement(LivyPreviewBanner, {
      companyLabel: companyLabel
    }), /*#__PURE__*/React.createElement(Tabs, {
      value: tab,
      onChange: (_, next) => setTab(next),
      sx: {
        px: '16px',
        minHeight: 36,
        borderBottom: 1,
        borderColor: 'divider',
        '& .MuiTab-root': {
          minHeight: 36,
          py: 0,
          textTransform: 'none'
        }
      }
    }, /*#__PURE__*/React.createElement(Tab, {
      value: "chat",
      label: "Chat"
    }), /*#__PURE__*/React.createElement(Tab, {
      value: "help",
      label: "Help",
      disabled: streaming
    }), /*#__PURE__*/React.createElement(Tab, {
      value: "reports",
      label: "Reports",
      disabled: streaming
    })), tab === 'reports' ? /*#__PURE__*/React.createElement(LivyReports, {
      billingType: billingType(),
      favorites: engagement === null || engagement === void 0 ? void 0 : engagement.favorites,
      mostRun: engagement === null || engagement === void 0 ? void 0 : engagement.favorite,
      onRun: prompt => {
        reset();
        setTab('chat');
        setQueued(prompt);
      }
    }) : tab === 'help' ? /*#__PURE__*/React.createElement(LivyHelp, {
      recent: engagement === null || engagement === void 0 ? void 0 : engagement.topics,
      onboarding: true,
      billingType: billingType(),
      supportFormUrl: engagement === null || engagement === void 0 ? void 0 : engagement.supportFormUrl,
      onAsk: startWith
    }) : /*#__PURE__*/React.createElement(LivyPanel, {
      live: live,
      onLive: startLive,
      conversationId: conversationId,
      surface: "pm",
      messages: messages,
      streaming: streaming,
      error: error,
      companyLabel: companyLabel,
      starters: SURFACE_STARTERS.pm,
      reach: "account",
      subtitle: SURFACE_SUBTITLE.pm,
      showScope: false,
      reports: popularReports(billingType(), engagement === null || engagement === void 0 ? void 0 : engagement.favorite),
      readOnly: readOnly,
      onSend: send,
      onStop: stop,
      onRetry: retry
    })), /*#__PURE__*/React.createElement(LivyHistory, {
      open: historyOpen,
      onClose: () => setHistoryOpen(false),
      onResume: (id, history, ran) => {
        // A staff thread from another surface would carry its reach into this company's customer view:
        // it reopens on the staff page, which restores the surface it ran on.
        if (staff && ran && ran !== 'pm') return navigate('/livy');
        setTab('chat');
        resume(id, history);
      }
    }))
  );
});
export function LivyCustomerPage() {
  return /*#__PURE__*/React.createElement(LivyTheme, null, /*#__PURE__*/React.createElement(LivyCustomerPageBody, null));
}